import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, homePath } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Clock, Loader2, ShieldCheck, Building2, User, FolderArchive } from "lucide-react";
import { toast } from "sonner";
import Logo from "@/components/Logo";

const DEMO = [
  { label: "Empresa", email: "admin@iberica.com", password: "Empresa1234", icon: Building2 },
  { label: "Empleado", email: "ana@iberica.com", password: "Empleado1234", icon: User },
  { label: "Gestoría", email: "gestoria@iberica.com", password: "Gestoria1234", icon: FolderArchive },
];

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) nav(homePath(user.role), { replace: true });
  }, [user, nav]);

  const submit = async (e, creds) => {
    if (e) e.preventDefault();
    const em = creds?.email ?? email;
    const pw = creds?.password ?? password;
    setLoading(true);
    try {
      const u = await login(em, pw);
      toast.success(`Bienvenido, ${u.name}`);
      nav(homePath(u.role), { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Error al iniciar sesión");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left brand panel */}
      <div className="hidden lg:flex w-1/2 bg-slate-900 text-white p-14 flex-col justify-between relative overflow-hidden">
        <div className="absolute -top-20 -right-20 h-96 w-96 rounded-full" style={{ background: "radial-gradient(circle, rgba(0,224,255,.30), transparent 70%)" }} />
        <div className="flex items-center gap-3 relative z-10">
          <Logo size={42} dark textSize="text-xl" tagline />
          <span className="ml-2 text-[10px] font-semibold tracking-wide uppercase bg-white/10 text-[#5a8fc7] px-2 py-1 rounded-full">Próximamente www.fichando.app</span>
        </div>
        <div className="relative z-10 max-w-md">
          <h1 className="font-heading text-5xl font-bold tracking-tighter leading-none">
            Control horario,<br />sin fricción.
          </h1>
          <p className="mt-6 text-slate-300 text-lg leading-relaxed">
            La plataforma marca blanca para gestionar fichajes, vacaciones y equipos.
            Multi-empresa, modo quiosco y analítica en tiempo real.
          </p>
          <div className="mt-10 flex gap-8 text-sm text-slate-400">
            <div><div className="font-heading text-2xl font-bold text-white">100%</div>Cumplimiento legal</div>
            <div><div className="font-heading text-2xl font-bold text-white">Real-time</div>Analítica visual</div>
            <div><div className="font-heading text-2xl font-bold text-white">Multi-tenant</div>Marca blanca</div>
          </div>
        </div>
        <div className="text-xs text-slate-500 relative z-10">© 2026 Fichando · www.fichando.app</div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-6 bg-[#F8FAFC]">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2 mb-8 justify-center">
            <Logo size={36} textSize="text-lg" />
          </div>
          <h2 className="font-heading text-3xl font-bold tracking-tight">Inicia sesión</h2>
          <p className="text-slate-500 mt-1 text-sm">Accede a tu panel de control.</p>

          <form onSubmit={(e) => submit(e)} className="mt-8 space-y-4">
            <div>
              <Label htmlFor="email" className="text-slate-700">Email</Label>
              <Input
                id="email" type="email" required value={email} data-testid="login-email"
                onChange={(e) => setEmail(e.target.value)} placeholder="tu@empresa.com"
                className="h-11 mt-1.5 bg-slate-50 border-transparent focus:bg-white focus:border-[#003366]"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-slate-700">Contraseña</Label>
              <Input
                id="password" type="password" required value={password} data-testid="login-password"
                onChange={(e) => setPassword(e.target.value)} placeholder="••••••••"
                className="h-11 mt-1.5 bg-slate-50 border-transparent focus:bg-white focus:border-[#003366]"
              />
            </div>
            <Button type="submit" disabled={loading} data-testid="login-submit" className="w-full h-11 brand-btn">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Entrar"}
            </Button>
          </form>

          <div className="mt-8">
            <div className="text-xs font-bold tracking-[0.15em] uppercase text-slate-400 text-center mb-3">Acceso demo</div>
            <div className="grid grid-cols-3 gap-2">
              {DEMO.map((d) => (
                <button
                  key={d.label} type="button" disabled={loading}
                  data-testid={`demo-${d.label.toLowerCase()}`}
                  onClick={() => submit(null, d)}
                  className="flex flex-col items-center gap-1.5 p-3 rounded-xl border border-slate-200 bg-white hover:border-[#2e6bb0] hover:bg-[#eaf2fb]/50 transition-all text-xs font-medium text-slate-600"
                >
                  <d.icon className="h-4 w-4 text-[#003366]" />
                  {d.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
